import { HoverCard } from '@mantine/core';
import { CADDIE_Z_INDEX } from '../../constants/z-index';
import type { Citation } from '../services/types';
import { CitationSourcesPopover } from './CitationSourcesPopover';

const CITATION_BLUE = '#5071f5';
const CHIP_SIZE_PX = 18;
const CHIP_RADIUS_PX = 2;
const ICON_SIZE_PX = 12;
const POPOVER_WIDTH_PX = 200;
const POPOVER_RADIUS_PX = 8;
const POPOVER_SURFACE = '#0a0a0a';
const HOVER_CLOSE_DELAY_MS = 150;

const CitationLinkIcon = () => (
  <svg
    width={ICON_SIZE_PX}
    height={ICON_SIZE_PX}
    viewBox="3 3 12 12"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7.99998 9.5C8.2147 9.78706 8.48866 10.0246 8.80325 10.1965C9.11785 10.3683 9.46573 10.4706 9.8233 10.4962C10.1809 10.5218 10.5398 10.4702 10.8757 10.3449C11.2115 10.2196 11.5165 10.0235 11.77 9.77L13.27 8.27C13.7254 7.79849 13.9774 7.16699 13.9717 6.51149C13.966 5.856 13.703 5.22897 13.2395 4.76545C12.776 4.30193 12.149 4.03901 11.4935 4.03331C10.838 4.02762 10.2065 4.2796 9.73498 4.735L8.87498 5.59M9.99998 8.5C9.78525 8.21293 9.5113 7.97541 9.1967 7.80353C8.88211 7.63165 8.53422 7.52944 8.17665 7.50383C7.81908 7.47822 7.46018 7.52981 7.12431 7.65511C6.78843 7.7804 6.48342 7.97646 6.22998 8.23L4.72998 9.73C4.27458 10.2015 4.0226 10.833 4.02829 11.4885C4.03399 12.144 4.29691 12.771 4.76043 13.2345C5.22395 13.6981 5.85098 13.961 6.50647 13.9667C7.16197 13.9724 7.79347 13.7204 8.26498 13.265L9.11998 12.41"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CitationMarkerProps {
  citations: Citation[];
  animationClass?: string;
}

export const CitationMarker = ({
  citations,
  animationClass = 'caddie-citation-enter',
}: CitationMarkerProps) => {
  if (citations.length === 0) return null;

  const sourceLabel = `${citations.length} source${
    citations.length > 1 ? 's' : ''
  }`;

  return (
    <HoverCard
      position="top"
      withArrow
      arrowSize={12}
      openDelay={0}
      closeDelay={HOVER_CLOSE_DELAY_MS}
      offset={8}
      width={POPOVER_WIDTH_PX}
      radius={POPOVER_RADIUS_PX}
      shadow="md"
      withinPortal={false}
      zIndex={CADDIE_Z_INDEX.MENU}
      styles={{
        dropdown: {
          padding: 0,
          backgroundColor: POPOVER_SURFACE,
          border: 'none',
        },
        arrow: { backgroundColor: POPOVER_SURFACE, border: 'none' },
      }}
    >
      <HoverCard.Target>
        <button
          type="button"
          aria-label={sourceLabel}
          aria-haspopup="dialog"
          className={`caddietw-inline-flex caddietw-items-center caddietw-justify-center caddietw-cursor-pointer caddietw-bg-[color:color-mix(in_srgb,var(--caddie-citation-blue)_10%,transparent)] caddietw-text-[color:var(--caddie-citation-blue)] hover:caddietw-text-white hover:caddietw-bg-[color:var(--caddie-citation-blue)] focus-visible:caddietw-text-white focus-visible:caddietw-bg-[color:var(--caddie-citation-blue)] focus-visible:caddietw-outline focus-visible:caddietw-outline-2 focus-visible:caddietw-outline-offset-2 focus-visible:caddietw-outline-[color:var(--caddie-citation-blue)] ${animationClass}`}
          style={
            {
              '--caddie-citation-blue': CITATION_BLUE,
              width: CHIP_SIZE_PX,
              height: CHIP_SIZE_PX,
              borderRadius: CHIP_RADIUS_PX,
              border: 'none',
              padding: 0,
              margin: 0,
              font: 'inherit',
              appearance: 'none',
            } as React.CSSProperties
          }
        >
          <CitationLinkIcon />
        </button>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <CitationSourcesPopover citations={citations} />
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
